import { supabase } from '@/lib/supabase'
import { useQuery } from '@tanstack/react-query'

export interface HeroBackgrounds {
  home: string
  guides: string
  tours: string
  trek: string
  about: string
  faq: string
  contact: string
}

const STORAGE_KEY = 'hero-backgrounds-cache'

const defaultBackgrounds: HeroBackgrounds = {
  home: '',
  guides: '',
  tours: '',
  trek: '',
  about: '',
  faq: '',
  contact: '',
}

export const useHeroBackgrounds = () => {
  // Get cached data from localStorage for instant initial render
  const getCachedData = (): HeroBackgrounds => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY)
      return cached ? JSON.parse(cached) : defaultBackgrounds
    } catch {
      return defaultBackgrounds
    }
  }

  return useQuery(
    ['hero-backgrounds'],
    async (): Promise<HeroBackgrounds> => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'hero_backgrounds')
        .single()

      if (error) {
        console.error('Error fetching hero backgrounds:', error)
        // Return default empty backgrounds if not found
        return defaultBackgrounds
      }

      // Cache the data in localStorage for next page load
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data.value))
      } catch (e) {
        console.error('Failed to cache hero backgrounds:', e)
      }

      return data.value as HeroBackgrounds
    },
    {
      placeholderData: getCachedData(),
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes (cacheTime in React Query v4)
    }
  )
}
